import React from 'react';
import type { ApolloError } from '@apollo/client';
import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';

import { client } from '~/api/client';
import {
  type AmsBrowseAccountFieldsFragment,
  type AmsRefInput,
  type EmailAmsChoiceFieldsFragment,
  ImportAmsAccountForOutboundEmailDocument,
  type ImportAmsAccountForOutboundEmailMutation,
  type ImportAmsAccountForOutboundEmailMutationVariables,
  ImportEmailAmsAccountDocument,
  type ImportEmailAmsAccountMutation,
  type ImportEmailAmsAccountMutationVariables,
  SearchAmsAccountsForOutboundEmailDocument,
  type SearchAmsAccountsForOutboundEmailQuery,
  type SearchAmsAccountsForOutboundEmailQueryVariables,
  SearchEmailAmsAccountsDocument,
  type SearchEmailAmsAccountsQuery,
  type SearchEmailAmsAccountsQueryVariables
} from '~/api/graphql';
import { useDebouncedValue } from '~/hooks/use-list-controls';

import { type ChosenClient, ClientList, type ClientOption, refInputOf } from './client-list';

/**
 * What Choose client searches and imports through: the draft's workspace while composing,
 * or the sent email while pushing it, which may have no workspace the pusher can read.
 */
export type ClientSearchScope = { kind: 'workspace' | 'outboundEmail'; id: string };

type SearchedAccount = AmsBrowseAccountFieldsFragment;

type ScopedAnswer<T> = { errors?: readonly unknown[]; answer: T | undefined };

const searchClients = async (
  scope: ClientSearchScope,
  query: string
): Promise<ScopedAnswer<SearchedAccount[]>> => {
  if (scope.kind === 'outboundEmail') {
    const result = await client.query<
      SearchAmsAccountsForOutboundEmailQuery,
      SearchAmsAccountsForOutboundEmailQueryVariables
    >({
      query: SearchAmsAccountsForOutboundEmailDocument,
      variables: { outboundEmailId: scope.id, query },
      fetchPolicy: 'no-cache'
    });
    return { errors: result.errors, answer: result.data?.searchAmsAccountsForOutboundEmail };
  }
  const result = await client.query<
    SearchEmailAmsAccountsQuery,
    SearchEmailAmsAccountsQueryVariables
  >({
    query: SearchEmailAmsAccountsDocument,
    variables: { workspaceId: scope.id, query },
    fetchPolicy: 'no-cache'
  });
  return { errors: result.errors, answer: result.data?.searchEmailAmsAccounts };
};

const importClient = async (
  scope: ClientSearchScope,
  account: AmsRefInput
): Promise<ScopedAnswer<EmailAmsChoiceFieldsFragment>> => {
  if (scope.kind === 'outboundEmail') {
    const result = await client.mutate<
      ImportAmsAccountForOutboundEmailMutation,
      ImportAmsAccountForOutboundEmailMutationVariables
    >({
      mutation: ImportAmsAccountForOutboundEmailDocument,
      variables: { outboundEmailId: scope.id, account }
    });
    return { errors: result.errors, answer: result.data?.importAmsAccountForOutboundEmail };
  }
  const result = await client.mutate<
    ImportEmailAmsAccountMutation,
    ImportEmailAmsAccountMutationVariables
  >({
    mutation: ImportEmailAmsAccountDocument,
    variables: { workspaceId: scope.id, account }
  });
  return { errors: result.errors, answer: result.data?.importEmailAmsAccount };
};

const SEARCH_SETTLE_MS = 300;
const SEARCH_RESULT_LIMIT = 25;

const optionOfSearched = (account: SearchedAccount): ClientOption => ({
  target: refInputOf(account.ref),
  displayName: account.name,
  detail: [account.email, account.lookupCode].filter(Boolean).join(' · ') || undefined,
  isSearched: true
});

/** The search finds a client by a word at least this long (searchintegration's minTokenLength). */
const MIN_SEARCH_WORD_LENGTH = 3;

const CONNECTION_HINT = 'Check your connection and try again.';

const hasSearchableWord = (query: string) =>
  query.split(/\s+/).some((word) => word.length >= MIN_SEARCH_WORD_LENGTH);

/** Searches the logging AMS's clients once the query settles. */
export const useClientSearch = ({ kind, id }: ClientSearchScope, query: string) => {
  const trimmed = query.trim();
  const isTooShort = trimmed !== '' && !hasSearchableWord(trimmed);
  const settledQuery = useDebouncedValue(isTooShort ? '' : trimmed, SEARCH_SETTLE_MS);
  const [answer, setAnswer] = React.useState<{ query: string; accounts: SearchedAccount[] }>();
  const [failure, setFailure] = React.useState<{ query: string; message: string } | null>(null);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    if (!settledQuery) return;
    let current = true;
    setFailure(null);
    searchClients({ kind, id }, settledQuery)
      .then((result) => {
        if (!current) return;
        const refusal = messageOfFailure(result, CONNECTION_HINT);
        if (refusal || !result.answer) {
          setFailure({ query: settledQuery, message: refusal ?? CONNECTION_HINT });
          return;
        }
        setAnswer({ query: settledQuery, accounts: result.answer });
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error(error);
        if (current)
          setFailure({ query: settledQuery, message: messageFromError(error, CONNECTION_HINT) });
      });
    return () => {
      current = false;
    };
  }, [kind, id, settledQuery, attempt]);

  const failureMessage = failure?.query === trimmed ? failure.message : null;
  return {
    accounts: answer?.query === trimmed ? answer.accounts : null,
    isTooShort,
    isSearching: trimmed !== '' && !isTooShort && !failureMessage && answer?.query !== trimmed,
    failureMessage,
    retry: () => setAttempt((count) => count + 1)
  };
};

export const ClientSearchResults = (props: {
  amsName: string;
  query: string;
  search: ReturnType<typeof useClientSearch>;
  /** Clients already listed above, left out so each shows once. */
  listedKeys: ReadonlySet<string>;
  pickedKey: string | undefined;
  onPick: (option: ClientOption) => void;
}) => {
  const { amsName, search } = props;
  if (search.isTooShort) {
    return (
      <div className="flex min-h-40 items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
        Type at least {MIN_SEARCH_WORD_LENGTH} letters to search.
      </div>
    );
  }
  if (search.isSearching) {
    return (
      <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
        <Spinner size="size-4" color="neutral" />
        Searching {amsName}…
      </div>
    );
  }
  if (search.failureMessage) {
    return (
      <div
        role="alert"
        className="flex min-h-40 flex-col items-center justify-center gap-2 text-center text-sm"
      >
        <span className="font-semibold text-red-700 dark:text-red-400">
          Couldn&apos;t search {amsName}.
        </span>
        <span className="text-neutral-500 dark:text-neutral-400">{search.failureMessage}</span>
        <Button color="white" onClick={search.retry}>
          Try again
        </Button>
      </div>
    );
  }
  if (!search.accounts) return null;
  if (search.accounts.length === 0) {
    return (
      <div className="flex min-h-40 flex-col items-center justify-center px-4 text-center text-sm">
        <span className="font-semibold text-neutral-900 dark:text-neutral-100">
          No {amsName} clients match &quot;{props.query.trim()}&quot;.
        </span>
        <span className="mt-1 text-neutral-500 dark:text-neutral-400">
          Check the spelling, or try their business name. A client added to {amsName} in the last
          few minutes may not show up yet.
        </span>
      </div>
    );
  }
  const unlisted = search.accounts
    .map(optionOfSearched)
    .filter((option) => !props.listedKeys.has(option.target.externalId));
  return (
    <>
      {search.accounts.length >= SEARCH_RESULT_LIMIT && (
        <p className="px-4 py-2 text-sm text-neutral-500 dark:text-neutral-400">
          Showing up to {SEARCH_RESULT_LIMIT} clients. Narrow your search if you don&apos;t see the
          client.
        </p>
      )}
      {unlisted.length > 0 && (
        <ClientList
          label={`${amsName} clients`}
          options={unlisted}
          pickedKey={props.pickedKey}
          onPick={props.onPick}
        />
      )}
    </>
  );
};

/**
 * Adds a searched client to Cara, because an email can only be logged to a client it
 * knows. Resolves to the client, or to the reason it couldn't be added.
 */
export const importSearchedClient = async (
  scope: ClientSearchScope,
  option: ClientOption,
  amsName: string
): Promise<ChosenClient | string> => {
  const couldNotAdd = `Couldn't add that client from ${amsName}. Try again.`;
  const unreachable = `Couldn't add that client from ${amsName}. ${CONNECTION_HINT}`;
  try {
    const result = await importClient(scope, option.target);
    const imported = result.answer;
    const refusal = messageOfFailure(result, couldNotAdd);
    if (refusal || !imported?.target) return refusal ?? couldNotAdd;
    return {
      target: refInputOf(imported.target),
      displayName: imported.displayName ?? option.displayName
    };
  } catch (failure) {
    // eslint-disable-next-line no-console
    console.error(failure);
    const isUnreachable = !!(failure as ApolloError | undefined)?.networkError;
    return isUnreachable ? unreachable : messageFromError(failure, couldNotAdd);
  }
};
